import { readonly, ref } from "@vue/composition-api";
import axios from "@/libs/axios";
// @ts-ignore
import { getUserData } from "@/auth/getUserData";
import { useToast } from "vue-toastification/composition";
// @ts-ignore
import ToastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
// @ts-ignore
import i18n from "@/libs/i18n";
import { MediaTypeType } from "@/types/mediaType.type";
import ChannelInterface from "@/interfaces/channel.interface";
import { resolveFilesizeLimit } from "@/utils/channels";

const state = ref([]);

export default function useFileUploads() {
    const toast = useToast();

    const loading = ref(false);

    // Will upload all the files to the same path if a path has been passed
    const uploadFiles = async (assets: File[], channel: ChannelInterface, path?: string) => {
        loading.value = true;

        if (!channel.provider || !channel.platform) {
            throw new Error("Missing channel data for uploading media");
        }

        await Promise.all(
            assets.map((asset) => {
                return uploadSingle(
                    asset,
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    resolveFilesizeLimit(channel.provider!, channel.platform!, asset.type),
                    { platform: channel.platform, provider: channel.provider },
                    path
                );
            })
        )
            // @ts-ignore
            .then((response) => {
                // @ts-ignore
                response.map((response) => state.value.push(response));

                toast({
                    component: ToastificationContent,
                    props: {
                        title: i18n.t("upload.Upload"),
                        text: i18n.tc("upload.success", response.length),
                        icon: "check",
                        variant: "success",
                    },
                });
            })
            // @ts-ignore
            .catch((error) =>
                toast({
                    component: ToastificationContent,
                    props: {
                        title: i18n.t("upload.Upload"),
                        text: error,
                        icon: "times",
                        variant: "danger",
                    },
                })
            );

        loading.value = false;
    };

    const clearFiles = () => {
        state.value = [];
    };

    const removeFile = (file: any) => {
        const fileIndex = state.value.findIndex((findFile) => findFile === file);

        state.value.splice(fileIndex, 1);
    };

    const uploadSingle = (
        asset: any,
        sizeLimit: number,
        channel?: { platform: string; provider: string },
        path?: string
    ) => {
        return new Promise(async (resolve, reject) => {
            // If the provided asset is not an instance of FormData, we're directly uploading an asset instanceof File.
            if (!(asset instanceof File)) {
                return reject(i18n.t("entities.asset.instance-error", { filename: asset.name }));
            }

            // Size limit is in MB
            if (asset.size > 1048576 * sizeLimit) {
                const assetType = asset.type.split("/")[0].toLowerCase();
                const supportedFileTypes = ["image", "application", "text", "video", "audio"];

                return reject(
                    i18n.t("entities.asset.filesize-error", {
                        filename: asset.name,
                        size: sizeLimit,
                        filetype: i18n.t(
                            `entities.asset.file-types.${
                                supportedFileTypes.includes(assetType) ? assetType : "default"
                            }`
                        ),
                    })
                );
            }

            const fileToCheck = new FormData();
            fileToCheck.append("file", asset);
            fileToCheck.append("channel", JSON.stringify(channel));

            const assetSupported = await axios
                .post(`${process.env.VUE_APP_API_GATEWAY}/assets/is-asset-supported`, fileToCheck, {
                    // Convert size limit to mb
                    maxBodyLength: 1048576 * sizeLimit,
                    maxContentLength: 1048576 * sizeLimit,
                })
                .then((result) => result.data)
                .catch((error) => reject(error));

            if (!assetSupported) {
                return reject(i18n.t("entities.asset.wrong-instance"));
            }

            const formData = new FormData();
            formData.append("file", asset);
            axios
                .post(
                    `${process.env.VUE_APP_API_GATEWAY}/assets/upload/storage-name/${getUserData().storageName}`,
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                        params: {
                            path: path,
                        },
                        // Convert size limit to mb
                        maxBodyLength: 1048576 * sizeLimit,
                        maxContentLength: 1048576 * sizeLimit,
                        // Uploading of assets can take a long time so timeout is 5 minutes
                        timeout: 300000,
                    }
                )
                // @ts-ignore
                .then((response) => resolve({ ...response.data, originalFileObject: asset }))
                // @ts-ignore
                .catch((error) => reject(error));
        });
    };

    const addAsset = (assetId: number) => {
        // @ts-ignore
        axios.get(`assets/${assetId}`).then((response) => state.value.push(response.data));
    };

    const getAllowedFileTypes = (
        mediaType: MediaTypeType,
        channel?: { platform: string; provider?: string }
    ): string[] => {
        let allowedImages: string[] = ["image/*"];
        let allowedVideo: string[] = ["video/*"];
        let allowedAudio: string[] = ["audio/*"];
        let allowedText: string[] = ["text/plain"];
        const allowedApplications: string[] = [
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.ms-powerpoint",
            "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        ];

        if (channel?.platform.toLowerCase() === "whatsapp" && channel?.provider?.toLowerCase() === "messagebird") {
            allowedVideo = ["video/mp4"];
            allowedAudio = ["audio/aac", "audio/mp4", "audio/mpeg", "audio/amr", "audio/ogg"];
            allowedImages = ["image/jpeg", "image/png"];
        } else if (channel?.platform.toLowerCase() === "whatsapp" && channel.provider?.toLowerCase() === "spryng") {
            allowedVideo = ["video/mp4", "video/3gp"];
            allowedAudio = ["audio/aac", "audio/mp4", "audio/mpeg", "audio/amr", "audio/ogg", "audio/opus"];
            allowedImages = ["image/jpeg", "image/png"];
            allowedText = ["text/plain"];
        }

        switch (mediaType) {
            case MediaTypeType.IMAGE:
                return allowedImages;
            case MediaTypeType.VIDEO:
                return allowedVideo;
            case MediaTypeType.DOCUMENT:
                return [allowedApplications, allowedText].flat();
            default:
                return [allowedImages, allowedVideo, allowedAudio, allowedApplications, allowedText].flat();
        }
    };

    return {
        isLoading: readonly(loading),
        getFiles: readonly(state),
        uploadFiles,
        uploadFile: uploadSingle,
        removeFile,
        clearFiles,
        addAsset,
        getAllowedFileTypes,
    };
}
